/**
 * "AI is working on it" bubble — three bouncing dots and a label.
 *
 * The dots animate via inline keyframes (no Tailwind config change).
 * `prefers-reduced-motion` is honoured because the keyframes live
 * inside `aim-shimmer` and the global `@media` rule in `app.css`
 * disables our shimmer — for the dots we add a parallel guard inline.
 *
 * Three variants share the same look, different labels:
 *   - capturing: "Saving and processing…"  (after a user note POST)
 *   - thinking:  "Searching memory…"        (after an /ask)
 *   - digesting: "Building digest…"        (after /today or /week)
 */
import type { PendingMessage } from "../types";

const LABELS: Record<PendingMessage["variant"], string> = {
  capturing: "Saving and processing…",
  thinking: "Searching memory…",
  digesting: "Building digest…",
};

interface Props {
  message: PendingMessage;
}

export function ThinkingBubble({ message }: Props): React.ReactElement {
  return (
    <div className="flex flex-col items-start gap-0.5 px-3 py-0.5 max-w-[88%]">
      <div
        className="rounded-[18px] rounded-bl-[6px] px-3 py-2 text-[13px] leading-[18px] inline-flex items-center gap-2"
        style={{
          background: "var(--color-secondary-bg)",
          color: "var(--color-hint)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <Dots />
        <span>{LABELS[message.variant]}</span>
      </div>
    </div>
  );
}

function Dots(): React.ReactElement {
  return (
    <span className="inline-flex items-end gap-[3px] h-4" aria-hidden="true">
      <Dot delay="0ms" />
      <Dot delay="160ms" />
      <Dot delay="320ms" />
    </span>
  );
}

function Dot({ delay }: { delay: string }): React.ReactElement {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full inline-block"
      style={{
        background: "currentColor",
        animation: "aim-thinking-bounce 1.1s ease-in-out infinite",
        animationDelay: delay,
      }}
    />
  );
}
